import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Close,
  DeleteOutlined,
  FileDownloadOutlined,
  PhotoLibraryOutlined,
} from "@mui/icons-material";
import { GetObjectCommand } from "@aws-sdk/client-s3";
import PhotoImage from "../components/PhotoImage";
import PhotoGrid from "../components/PhotoGrid";
import { S3_PHOTOS_BUCKET } from "../media";
import { radius, tokens } from "../theme/theme";

/**
 * Photos of one listing.
 *
 * Was a single column with the download and delete buttons floating above each
 * image, which meant scrolling past every photo to reach the last one. Now a
 * thumbnail grid with the actions on hover, and a click opens the full-size
 * image with keyboard arrows.
 *
 * Thumbnails are the ~640px copies; only the lightbox and the download button
 * ever fetch the original.
 */
export default function StandardImageList({
  openPhotos,
  handleClosePhotos,
  photos,
  onDeletePhoto,
  onReorder,
  admin,
  s3Client,
}) {
  const [lightbox, setLightbox] = useState(-1);

  const count = photos ? photos.length : 0;

  const step = (delta) => {
    if (count === 0) return;
    setLightbox(function (current) {
      return (current + delta + count) % count;
    });
  };

  useEffect(
    function () {
      if (lightbox < 0) return;
      const onKey = (e) => {
        if (e.key === "ArrowRight") step(1);
        else if (e.key === "ArrowLeft") step(-1);
        else if (e.key === "Escape") setLightbox(-1);
      };
      window.addEventListener("keydown", onKey);
      return function () {
        window.removeEventListener("keydown", onKey);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lightbox, count]
  );

  const [downloading, setDownloading] = useState("");
  const [downloadError, setDownloadError] = useState("");

  /**
   * Fetch the photo through the panel's own signed S3 client, not through
   * img.leocity.estate.
   *
   * The CDN URL cannot be fetched: CloudFront does not forward the `Origin`
   * header to S3, so no `Access-Control-Allow-Origin` ever comes back, and
   * whichever request populates the cache first decides what every later one
   * gets — which is why this failed for some photos and not others. A signed
   * request goes straight to the bucket, whose own CORS rule does allow this
   * origin. <img> tags are unaffected and still come from the CDN.
   */
  const download = async (item) => {
    setDownloadError("");
    setDownloading(item.key);
    let url = "";
    let link = null;
    try {
      const data = await s3Client.send(
        new GetObjectCommand({ Bucket: S3_PHOTOS_BUCKET, Key: item.key })
      );
      // Body is a ReadableStream in the browser; Response normalises it.
      const blob = await new Response(data.Body).blob();

      url = window.URL.createObjectURL(blob);
      link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", item.name);
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      // The old code logged to the console and left the realtor staring at a
      // button that did nothing.
      setDownloadError("Не вдалося завантажити «" + item.name + "»: " + err.message);
      console.error(err);
    } finally {
      if (link && link.parentNode) link.parentNode.removeChild(link);
      if (url) setTimeout(function () { window.URL.revokeObjectURL(url); }, 30000);
      setDownloading("");
    }
  };

  const current = lightbox >= 0 && photos ? photos[lightbox] : null;

  return (
    <React.Fragment>
      <Dialog
        open={openPhotos}
        onClose={handleClosePhotos}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: radius.lg } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderBottom: "1px solid " + tokens.line,
            py: 2,
          }}
        >
          <PhotoLibraryOutlined sx={{ color: tokens.blue600 }} />
          <Box sx={{ flexGrow: 1, lineHeight: 1.2 }}>
            <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
              Фото
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.ink400 }}>
              {count > 0
                ? count + " — натисніть, щоб відкрити"
                : "Немає завантажених фото"}
            </Typography>
          </Box>
          <IconButton onClick={handleClosePhotos} aria-label="Закрити">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: tokens.canvas }}>
          {downloadError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={function () {
                setDownloadError("");
              }}
            >
              {downloadError}
            </Alert>
          )}
          {count === 0 ? (
            <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
              <PhotoLibraryOutlined sx={{ fontSize: 40, color: tokens.ink400 }} />
              <Typography variant="body2" sx={{ color: tokens.ink500 }}>
                Для цього оголошення ще немає фото.
              </Typography>
            </Stack>
          ) : (
            <Box sx={{ pt: 3 }}>
              <PhotoGrid
                photos={photos}
                onReorder={onReorder}
                onOpen={setLightbox}
                actions={[
                  {
                    key: "download",
                    label: "Завантажити оригінал",
                    // A full-size photo takes a moment to come down; without
                    // this the button looks inert and gets clicked again.
                    isBusy: function (item) {
                      return downloading === item.key;
                    },
                    icon: function (item) {
                      return downloading === item.key ? (
                        <CircularProgress size={16} sx={{ color: "inherit" }} />
                      ) : (
                        <FileDownloadOutlined sx={{ fontSize: 18 }} />
                      );
                    },
                    onClick: download,
                    sx: actionButtonSx,
                  },
                ].concat(
                  admin
                    ? [
                        {
                          key: "delete",
                          label: "Видалити фото",
                          icon: function () {
                            return <DeleteOutlined sx={{ fontSize: 18 }} />;
                          },
                          onClick: onDeletePhoto,
                          sx: {
                            ...actionButtonSx,
                            "&:hover": {
                              backgroundColor: tokens.red600,
                              color: tokens.surface,
                            },
                          },
                        },
                      ]
                    : []
                )}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Full-size view. Separate dialog so closing it keeps the grid open. */}
      <Dialog
        open={current !== null}
        onClose={() => setLightbox(-1)}
        maxWidth="lg"
        PaperProps={{
          sx: { backgroundColor: "transparent", boxShadow: "none" },
        }}
      >
        {current && (
          <Box sx={{ position: "relative", display: "flex" }}>
            <PhotoImage
              src={current.url}
              fallbackSrc={current.thumbUrl}
              alt={current.name}
              sx={{
                maxWidth: "100%",
                maxHeight: "85vh",
                display: "block",
                borderRadius: radius.md,
              }}
            />
            <IconButton
              onClick={() => setLightbox(-1)}
              sx={{ ...lightboxButtonSx, top: 8, right: 8 }}
              aria-label="Закрити"
            >
              <Close />
            </IconButton>
            {count > 1 && (
              <React.Fragment>
                <IconButton
                  onClick={() => step(-1)}
                  sx={{ ...lightboxButtonSx, top: "50%", left: 8, mt: "-20px" }}
                  aria-label="Попереднє"
                >
                  <ChevronLeft />
                </IconButton>
                <IconButton
                  onClick={() => step(1)}
                  sx={{ ...lightboxButtonSx, top: "50%", right: 8, mt: "-20px" }}
                  aria-label="Наступне"
                >
                  <ChevronRight />
                </IconButton>
              </React.Fragment>
            )}
          </Box>
        )}
      </Dialog>
    </React.Fragment>
  );
}

const actionButtonSx = {
  backgroundColor: "rgba(255,255,255,0.92)",
  color: tokens.ink700,
  "&:hover": { backgroundColor: tokens.surface },
};

const lightboxButtonSx = {
  position: "absolute",
  backgroundColor: "rgba(14,27,46,0.55)",
  color: "#fff",
  "&:hover": { backgroundColor: "rgba(14,27,46,0.8)" },
};
